/**
 * Gravity: nothing is drawn, the page itself gives way. Its pieces (lines of
 * code and their numbers, tabs, buttons, rows of the file tree, PDF pages)
 * drop to the bottom of the window one after another, bounce, lie in a heap
 * for a moment, then all spring back together. Each piece just gets a Web
 * Animation of the individual `translate` and `rotate` properties: the DOM
 * isn't touched, so CodeMirror's MutationObserver sees nothing and typing
 * carries on, and elements keep their own `transform`s.
 */
import { EditorView } from "@codemirror/view";
import { calm, rand } from "./scene";

/** Layers over a whole page or editor that show nothing themselves: PDF.js's text and link layers, CodeMirror's cursor and selection. */
const SKIP = ".textLayer, .annotationLayer, .annotationEditorLayer, .cm-layer";
/** These fall in one piece whatever their size: controls, rows, a line of code or its number, a PDF page. */
const WHOLE =
  "svg, img, canvas, video, button, a, input, select, textarea, [role=button], [role=tab], [role=menuitem], .tree-row, .cm-line, .cm-gutterElement, .pdfViewer .page";
/** Replaced elements can be moved even when they're inline; a plain inline box can't. */
const REPLACED = new Set(["svg", "img", "canvas", "video", "iframe", "input", "select", "textarea", "button"]);

/** Past this share of the window's area, height or width an element comes apart rather than falling whole, so columns and full-width bars break up. */
const BIG_AREA = 0.06;
const BIG_HEIGHT = 0.45;
const BIG_WIDTH = 0.35;
/** Plenty for an editor, a file tree and a PDF; past this the smallest pieces stay put. */
const MAX_PIECES = 300;
/** A pane that clips without scrolling is the floor for what's in it if it's at least this tall (a share of the window); from a shorter one things fall out of sight. */
const MIN_ROOM = 0.25;
/** Anything with less room than this under it is already on the floor. */
const MIN_FALL_PX = 6;

/** A fall from the top of the window to the bottom takes this long; shorter ones take less, as √distance. */
const FULL_FALL_MS = 600;
/** Each bounce lasts this share of the one before, and so reaches its square in height. */
const BOUNCE = 0.42;
/** Things give way one after another over this long. */
const MAX_DELAY_MS = 450;
/** How deep the heap gets, as a share of the window's height: small things land on each other rather than all on the floor. */
const HEAP = 0.12;
/** Pieces taller than this lie on the floor rather than up on the heap, so a PDF page doesn't float. */
const HEAP_SIZE_PX = 60;
/** Tilt and sideways skid are these over a piece's longest side: a button tumbles, a wide row barely tips. */
const TILT_K = 2000;
const MAX_TILT_DEG = 30;
const SKID_K = 2400;
const MAX_SKID_PX = 40;
/** A line of code can't tilt (see Piece.tilts), so it skids a little instead. */
const LINE_SKID_PX = 10;
/** Something already on the floor is jolted as the rest lands: it hops, and rocks enough to move its ends this far. */
const HOP_PX = 4;
const ROCK_PX = 3;
const MAX_ROCK_DEG = 4;
/** The most a piece tilts is at the top of its first bounce, this much past where it ends up. */
const WILDEST = 1.12;

/** Everything springs back at about this point. */
const SPRING_AT_MS = 2550;
const SPRING_JITTER_MS = 100;
const SPRING_MS = 750;
/** How far past home a piece flies on the way back. */
const OVERSHOOT_PX = 14;
/** A hidden tab doesn't run animations, so don't wait on them for ever: this long after starting, or after the tab comes back. */
const GUARD_MS = SPRING_AT_MS + SPRING_JITTER_MS + SPRING_MS + 1500;
/** With reduced motion nothing moves; the banner just shows for this long. */
const CALM_MS = 1500;

/** y = t²: speeding up on the way down. */
const FALL = "cubic-bezier(0.333, 0, 0.667, 0.333)";
/** y = 1 − (1 − t)²: slowing on the way up. */
const RISE = "cubic-bezier(0.333, 0.667, 0.667, 1)";
/** Yanked home: a quick launch that eases out into the overshoot. */
const SNAP = "cubic-bezier(0.5, 0, 0.15, 1)";
const SWAY = "cubic-bezier(0.45, 0, 0.55, 1)";
/** Over the editor, the PDF and their splitters, under menus, dialogs and the emote banners. */
const RAISED_Z = 50;

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** What the walk carries down from the panes an element is in. */
interface Bounds {
  /** What's in sight: the window, cut down by every pane that clips. */
  clip: Box;
  /** The bottom of the pane things land in. */
  floor: number;
  /** The right edge of the innermost pane that scrolls sideways. */
  wall: number;
}

interface Piece {
  el: Element;
  box: DOMRect;
  /** How far it can fall before it lands. */
  room: number;
  /** How far it can skid each way and stay in sight. */
  left: number;
  right: number;
  /**
   * How far right it can reach without widening a pane it scrolls in, which would flash a scrollbar where they take up
   * room. Leftwards is safe: overflow that way can't be scrolled to. Downwards is kept safe by landing on the pane's floor.
   */
  wall: number;
  /**
   * CodeMirror measures its lines with getBoundingClientRect whenever the text changes (anyone typing), and a tilted line
   * measures taller, which would throw its gutter and scroll position off. Moving a line doesn't change its height.
   */
  tilts: boolean;
  /** A flex or grid item can be raised, so a toolbar button falls in front of the editor rather than behind it. */
  raise: boolean;
}

interface Step {
  /** Milliseconds from the start. */
  at: number;
  x: number;
  y: number;
  /** Degrees. */
  turn: number;
  /** The easing on to the next step. */
  ease?: string;
}

const wait = (ms: number) => new Promise<void>((done) => setTimeout(done, ms));
const sign = () => (Math.random() < 0.5 ? -1 : 1);
const overlaps = (a: Box, b: Box) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
const intersect = (a: Box, b: Box): Box => ({
  left: Math.max(a.left, b.left),
  top: Math.max(a.top, b.top),
  right: Math.min(a.right, b.right),
  bottom: Math.min(a.bottom, b.bottom),
});

/** No alpha: "rgba(…, 0)", or "/ 0)" in the newer colour syntaxes. */
const clear = (color: string) => color === "transparent" || /^rgba\(.*,\s*0\)$|\/\s*0\)$/.test(color);
/** Whether an element draws a box of its own: a background, border or shadow. */
const boxed = (s: CSSStyleDeclaration) =>
  !clear(s.backgroundColor) ||
  s.backgroundImage !== "none" ||
  s.boxShadow !== "none" ||
  [s.borderTopWidth, s.borderRightWidth, s.borderBottomWidth, s.borderLeftWidth].some((w) => parseFloat(w) > 0);
const inline = (el: Element, s: CSSStyleDeclaration) => s.display === "inline" && !REPLACED.has(el.localName);
const scrolls = (overflow: string) => overflow === "auto" || overflow === "scroll";
const hides = (s: CSSStyleDeclaration) => [s.overflowX, s.overflowY].some((o) => o === "hidden" || o === "clip");
const pseudo = (el: Element) => ["::before", "::after"].some((p) => !/^(none|normal)$/.test(getComputedStyle(el, p).content));

/** Text laid out in the element itself, which would stay behind if its children fell without it. */
function hasText(el: Element) {
  for (const n of el.childNodes) {
    if (n instanceof Text ? n.data.trim() : n instanceof Element && inline(n, getComputedStyle(n))) return true;
  }
  return false;
}

/** The bounds for an element's children: a pane that clips or scrolls cuts down what's in sight, and may be what they land on. */
function inside(el: Element, box: DOMRect, s: CSSStyleDeclaration, b: Bounds, h: number): Bounds {
  if ((s.overflowX === "visible" && s.overflowY === "visible") || s.display === "inline") return b;
  const left = box.left + el.clientLeft;
  const top = box.top + el.clientTop;
  const pane = { left, top, right: left + el.clientWidth, bottom: top + el.clientHeight };
  return {
    clip: intersect(b.clip, pane),
    // Falling past the bottom of a pane that scrolls would lengthen it, so things land there; a pane that only clips is a floor if it's tall enough to fall in.
    floor: scrolls(s.overflowY) || pane.bottom - pane.top >= MIN_ROOM * h ? Math.min(b.floor, pane.bottom) : b.floor,
    wall: scrolls(s.overflowX) ? Math.min(b.wall, pane.right) : b.wall,
  };
}

/** The visible pieces of the page: break it up from the top until the bits are small, or can't come apart. */
function collect(root: Element): Piece[] {
  const w = innerWidth;
  const h = innerHeight;
  const pieces: Piece[] = [];
  const visit = (el: Element, b: Bounds, parent: CSSStyleDeclaration | null): void => {
    if (el.matches(SKIP)) return;
    const style = getComputedStyle(el);
    if (style.display === "none" || parseFloat(style.opacity) === 0) return;
    const kids = el.matches(WHOLE) ? [] : [...el.children];
    if (style.display === "contents") return kids.forEach((k) => visit(k, b, parent));
    const box = el.getBoundingClientRect();
    if (!box.width || !box.height || !overlaps(box, b.clip)) return;
    // A full-width bar comes apart into its buttons, but a strip that clips them (the tabs) falls whole, or they'd vanish as they fell out of it.
    const big = box.width * box.height > BIG_AREA * w * h || box.height > BIG_HEIGHT * h || (box.width > BIG_WIDTH * w && !hides(style));
    // Small containers only there for layout (a list, a group of buttons) come apart too, so each row and button falls by itself.
    const apart = kids.length > 0 && (big || (!boxed(style) && !hasText(el) && !hides(style)));
    if (apart || inline(el, style) || style.visibility !== "visible") {
      const next = inside(el, box, style, b, h);
      return kids.forEach((k) => visit(k, next, style));
    }
    // A spacer shows nothing, and would only take the place of a piece that does.
    if (!el.childElementCount && !REPLACED.has(el.localName) && !el.textContent?.trim() && !boxed(style) && !pseudo(el)) return;
    pieces.push({
      el,
      box,
      room: b.floor - box.bottom,
      left: box.left - b.clip.left,
      right: b.clip.right - box.right,
      wall: b.wall - box.right,
      tilts: !el.closest(".cm-content"),
      raise: !!parent && /flex|grid/.test(parent.display) && style.zIndex === "auto",
    });
  };
  visit(root, { clip: { left: 0, top: 0, right: w, bottom: h }, floor: h, wall: Infinity }, null);
  const area = (p: Piece) => p.box.width * p.box.height;
  return pieces.length > MAX_PIECES ? pieces.sort((a, b) => area(b) - area(a)).slice(0, MAX_PIECES) : pieces;
}

/** The most a box turned anywhere up to `deg` about its centre sticks out past each side, and below its bottom. */
function overhang(b: DOMRect, deg: number) {
  const r = (Math.abs(deg) * Math.PI) / 180;
  const across = Math.min(r, Math.atan2(b.height, b.width));
  const down = Math.min(r, Math.atan2(b.width, b.height));
  return {
    side: (b.width * Math.cos(across) + b.height * Math.sin(across) - b.width) / 2,
    under: (b.width * Math.sin(down) + b.height * Math.cos(down) - b.height) / 2,
  };
}

/** Heads home at about the same moment as everything else, flies a little past (up, then `settle` down) and comes to rest. */
function springBack(at: number, x: number, y: number, turn: number, overshoot: number, settle: number): Step[] {
  return [
    { at, x, y, turn, ease: SNAP },
    { at: at + SPRING_MS * 0.55, x: 0, y: -overshoot, turn: -turn * 0.22, ease: SWAY },
    { at: at + SPRING_MS * 0.8, x: 0, y: settle, turn: turn * 0.07, ease: SWAY },
    { at: at + SPRING_MS, x: 0, y: 0, turn: 0 },
  ];
}

/** The drop, two bounces (each a real parabola: ease in on the way down, out on the way up), the heap and the way back. */
function fall(p: Piece, h: number, springAt: number): Step[] {
  const { width, height } = p.box;
  const size = Math.max(width, height);
  let tilt = p.tilts ? sign() * rand(0.35, 1) * Math.min(MAX_TILT_DEG, TILT_K / size) : 0;
  const fits = (deg: number) => {
    const o = overhang(p.box, deg * WILDEST);
    return o.side <= p.wall && o.under <= p.room - MIN_FALL_PX;
  };
  while (tilt && !fits(tilt)) tilt = Math.abs(tilt) < 0.5 ? 0 : tilt / 2;
  const { side, under } = overhang(p.box, tilt * WILDEST);
  const most = p.tilts ? Math.min(MAX_SKID_PX, SKID_K / size) : LINE_SKID_PX;
  const skid = rand(-Math.min(most, Math.max(0, p.left - side)), Math.min(most, Math.max(0, Math.min(p.right, p.wall) - side)));
  // It comes to rest with its lowest corner on the floor, or up on the heap.
  const drop = p.room - under - Math.min((p.room - under) / 2, HEAP * h * Math.random() ** 2 * Math.min(1, HEAP_SIZE_PX / height));
  const down = FULL_FALL_MS * Math.sqrt(drop / h);
  const t0 = rand(0, MAX_DELAY_MS);
  const t1 = t0 + down;
  const t2 = t1 + down * BOUNCE;
  const t3 = t2 + down * BOUNCE;
  const t4 = t3 + down * BOUNCE ** 2;
  const t5 = t4 + down * BOUNCE ** 2;
  const overshoot = Math.min(OVERSHOOT_PX, drop * 0.12);
  return [
    { at: t0, x: 0, y: 0, turn: 0, ease: FALL },
    { at: t1, x: skid * 0.15, y: drop, turn: tilt * 0.55, ease: RISE },
    { at: t2, x: skid * 0.6, y: drop * (1 - BOUNCE ** 2), turn: tilt * WILDEST, ease: FALL },
    { at: t3, x: skid * 0.85, y: drop, turn: tilt * 0.92, ease: RISE },
    { at: t4, x: skid * 0.95, y: drop * (1 - BOUNCE ** 4), turn: tilt * 1.03, ease: FALL },
    { at: t5, x: skid, y: drop, turn: tilt },
    ...springBack(Math.max(springAt, t5 + 100), skid, drop, tilt, overshoot, overshoot * 0.3),
  ];
}

/** Something already on the floor can't fall, so it's jolted as the rest lands, and springs with everyone. */
function jolt(p: Piece, h: number, springAt: number): Step[] {
  const size = Math.max(p.box.width, p.box.height);
  let rock = p.tilts ? sign() * rand(0.5, 1) * Math.min(MAX_ROCK_DEG, (Math.atan(ROCK_PX / (size / 2)) * 180) / Math.PI) : 0;
  if (overhang(p.box, rock).side > p.wall) rock = 0;
  const hop = FULL_FALL_MS * Math.sqrt(HOP_PX / h);
  const t0 = rand(300, 900);
  const t1 = t0 + hop;
  const t2 = t1 + hop;
  const t3 = t2 + 150;
  const t4 = t3 + 150;
  return [
    { at: t0, x: 0, y: 0, turn: 0, ease: RISE },
    { at: t1, x: 0, y: -HOP_PX, turn: rock, ease: FALL },
    { at: t2, x: 0, y: 0, turn: -rock * 0.6, ease: SWAY },
    { at: t3, x: 0, y: 0, turn: rock * 0.35, ease: SWAY },
    { at: t4, x: 0, y: 0, turn: rock * 0.2 },
    ...springBack(Math.max(springAt, t4 + 100), 0, 0, rock * 0.2, HOP_PX * 0.75, 0),
  ];
}

/** One animation per piece, delayed to its first step, that leaves nothing behind when it ends. */
function run(p: Piece, steps: Step[]): Animation {
  const start = steps[0].at;
  const span = steps[steps.length - 1].at - start;
  const frames = steps.map((s): Keyframe => {
    const frame: Keyframe = { offset: (s.at - start) / span, easing: s.ease ?? "linear", translate: `${s.x.toFixed(1)}px ${s.y.toFixed(1)}px` };
    if (p.tilts) frame.rotate = `${s.turn.toFixed(2)}deg`;
    if (p.raise) frame.zIndex = RAISED_Z;
    return frame;
  });
  return p.el.animate(frames, { duration: span, delay: start, fill: "none" });
}

/** Each editor on the page, as it was before the fall. */
function editorsIn(root: Element) {
  return [...root.querySelectorAll<HTMLElement>(".cm-editor")].flatMap((dom) => {
    const view = EditorView.findFromDOM(dom);
    return view ? [{ view, state: view.state }] : [];
  });
}

/**
 * An editor whose text or selection changed during the fall (anyone typing) drew its cursor and selection where the text
 * had fallen to, and won't draw them again until the next change. Setting the same selection redraws them where it is now.
 */
function redraw(editors: ReturnType<typeof editorsIn>) {
  for (const { view, state } of editors) {
    const moved = view.state.doc !== state.doc || view.state.selection !== state.selection;
    if (moved && view.dom.isConnected && !view.composing) view.dispatch({ selection: view.state.selection });
  }
}

export default async function play(): Promise<void> {
  const root = document.querySelector(".workspace") ?? document.getElementById("root");
  if (calm() || !root) return wait(CALM_MS);
  const pieces = collect(root);
  if (!pieces.length) return wait(CALM_MS);
  const editors = editorsIn(root);
  const h = innerHeight;
  const animations = pieces.map((p) => {
    const springAt = SPRING_AT_MS + rand(0, SPRING_JITTER_MS);
    return run(p, p.room < MIN_FALL_PX ? jolt(p, h, springAt) : fall(p, h, springAt));
  });

  return new Promise((done) => {
    let guard = 0;
    let over = false;
    const arm = () => {
      clearTimeout(guard);
      guard = window.setTimeout(finish, GUARD_MS);
    };
    // A hidden tab starts its animations when it comes back, so give them their full time from then.
    const onShow = () => document.visibilityState === "visible" && arm();
    const finish = () => {
      if (over) return;
      over = true;
      clearTimeout(guard);
      document.removeEventListener("visibilitychange", onShow);
      for (const a of animations) a.cancel();
      done();
      redraw(editors);
    };
    arm();
    document.addEventListener("visibilitychange", onShow);
    Promise.all(animations.map((a) => a.finished.catch(() => {}))).then(finish);
  });
}
